import React, { useState, useLayoutEffect, useRef } from 'react';
import { Client } from '../types';
import { clientChartAddressSave, legacyServiceAddressForReview } from '../utils/clientChartAddress';
import { chartEditGuardianRelationship } from '../utils/chartContactEdit';
import { resolveClientPocStreams, toPocDateInputValue } from '../utils/pocConflict';
import { X, Save, User, MapPin, Stethoscope, Users, Calendar, Shield } from 'lucide-react';

const SINGLE_POC_STATUSES = ['ACTIVE', 'EXPIRING', 'EXPIRED', 'PENDING_PHYSICIAN', 'PENDING_RECERT'];
const STREAM_POC_STATUSES = [...SINGLE_POC_STATUSES, 'INACTIVE'];

function editablePocDate(value?: string | null): string {
  const text = String(value ?? '').trim();
  if (!text || text.includes(';')) return '';
  const iso = toPocDateInputValue(text);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '';
}

function statusChoices(current: string, choices: string[]): string[] {
  return choices.includes(current) ? choices : [...choices, current];
}

export type ChartEditFocusSection = 'general' | 'poc' | 'speech' | 'feeding' | 'physician' | 'sc';

const CHART_EDIT_SECTION_TARGET: Record<Exclude<ChartEditFocusSection, 'general'>, string> = {
  poc: 'edit-modal-section-poc',
  speech: 'edit-modal-section-speech',
  feeding: 'edit-modal-section-feeding',
  physician: 'edit-modal-section-physician',
  sc: 'edit-modal-sc-section'
};

interface ClientEditModalProps {
  client: Client;
  onSave: (updated: Client) => void;
  onClose: () => void;
  initialFocusSection?: ChartEditFocusSection;
}

export const ClientEditModal: React.FC<ClientEditModalProps> = ({
  client,
  onSave,
  onClose,
  initialFocusSection = 'general'
}) => {
  const [formData, setFormData] = useState({
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    dob: client.dob || '',
    gender: client.gender || 'Male',
    status: client.status || 'ACTIVE',
    primary_language: client.primary_language || 'English',
    district_code: client.district_code || '',
    ahcccs_id: client.ahcccs_id || '',
    wellsky_caseno: client.wellsky_caseno || '',
    wellsky_client_id: client.wellsky_client_id || '',
    policy_id: client.policy_id || '',
    primary_payer_name: client.primary_payer_name || 'Arizona Medicaid (Mercy Care)',
    payer_type: client.payer_type || 'MEDICAID',
    tpl_status: client.tpl_status || 'NONE',
    secondary_payer_name: client.secondary_payer_name || '',
    secondary_payer_id: client.secondary_payer_id || '',
    secondary_policy_id: client.secondary_policy_id || '',
    secondary_group_id: client.secondary_group_id || '',
    insured_name: client.insured_name || '',
    insured_dob: client.insured_dob || '',
    subscriber_relationship: client.subscriber_relationship || '',
    service_address: client.service_address || '',
    home_address: client.home_address || '',
    address_line1: client.address_line1 || '',
    city: client.city || '',
    state: client.state || '',
    zip: client.zip || '',
    phone: client.phone || '',
    email: client.email || '',
    emergency_contact_name: client.emergency_contact_name || '',
    emergency_contact_phone: client.emergency_contact_phone || '',
    emergency_contact_relationship: client.emergency_contact_relationship || '',
    guardian_name: client.guardian_name || '',
    guardian_relationship: chartEditGuardianRelationship(client.guardian_relationship),
    guardian_phone: client.guardian_phone || '',
    guardian_email: client.guardian_email || '',
    guardian_address: client.guardian_address || '',
    diagnosis_code: client.diagnosis_code || '',
    diagnosis_description: client.diagnosis_description || '',
    referral_order_info: client.referral_order_info || '',
    physician_name: client.physician_name || '',
    physician_npi: client.physician_npi || '',
    physician_practice: client.physician_practice || '',
    physician_phone: client.physician_phone || '',
    physician_fax: client.physician_fax || '',
    support_coordinator_name: client.support_coordinator_name || '',
    support_coordinator_phone: client.support_coordinator_phone || '',
    support_coordinator_email: client.support_coordinator_email || ''
  });
  const initialPoc = resolveClientPocStreams(client);
  const [pocEdited, setPocEdited] = useState(false);
  const [clearStoredPocDates, setClearStoredPocDates] = useState(false);
  const [speechStart, setSpeechStart] = useState(initialPoc.mode === 'unresolved' ? '' : editablePocDate(initialPoc.speech.poc_start_date));
  const [speechEnd, setSpeechEnd] = useState(initialPoc.mode === 'unresolved' ? '' : editablePocDate(initialPoc.speech.poc_end_date));
  const [speechStatus, setSpeechStatus] = useState(initialPoc.mode === 'unresolved' ? (client.poc_status || 'ACTIVE') : initialPoc.speech.poc_status);
  const [feedingStart, setFeedingStart] = useState(initialPoc.mode === 'separate' ? editablePocDate(initialPoc.feeding.poc_start_date) : '');
  const [feedingEnd, setFeedingEnd] = useState(initialPoc.mode === 'separate' ? editablePocDate(initialPoc.feeding.poc_end_date) : '');
  const [feedingStatus, setFeedingStatus] = useState(initialPoc.mode === 'separate' ? initialPoc.feeding.poc_status : 'ACTIVE');
  const [addingFeeding, setAddingFeeding] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useLayoutEffect(() => {
    const form = formRef.current;
    if (!form) return;
    if (initialFocusSection === 'general') {
      form.scrollTop = 0;
      document.getElementById('edit-modal-section-demographics')?.focus({ preventScroll: true });
      return;
    }
    const targetId = CHART_EDIT_SECTION_TARGET[initialFocusSection];
    const target = document.getElementById(targetId)
      || (initialFocusSection === 'feeding' ? document.getElementById('edit-modal-section-poc') : null);
    if (!target) {
      form.scrollTop = 0;
      return;
    }
    const alignToFormTop = target.getBoundingClientRect().top - form.getBoundingClientRect().top;
    form.scrollTop += alignToFormTop;
    target.focus({ preventScroll: true });
  }, [initialFocusSection]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const addressSave = clientChartAddressSave({
      street: formData.address_line1,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      serviceAddress: client.service_address,
      homeAddress: formData.home_address
    });
    if (addressSave.error) {
      setError(addressSave.error);
      setSaving(false);
      return;
    }

    const pocPayload = !pocEdited && !addingFeeding
      ? {}
      : initialPoc.mode === 'unresolved'
        ? {
            poc_dates_edited: true,
            poc_dates_cleared: clearStoredPocDates,
            poc_status: speechStatus
          }
        : initialPoc.mode === 'separate'
          ? {
              poc_dates_edited: true,
              poc_start_date: speechStart,
              poc_end_date: speechEnd,
              poc_status: speechStatus,
              feeding_poc_start_date: feedingStart,
              feeding_poc_end_date: feedingEnd,
              feeding_poc_status: feedingStatus
            }
          : addingFeeding
            ? {
                poc_dates_edited: true,
                feeding_poc_added: true,
                poc_start_date: speechStart,
                poc_end_date: speechEnd,
                poc_status: speechStatus,
                feeding_poc_start_date: feedingStart,
                feeding_poc_end_date: feedingEnd,
                feeding_poc_status: feedingStatus
              }
            : {
                poc_dates_edited: true,
                poc_start_date: speechStart,
                poc_end_date: speechEnd,
                poc_status: speechStatus
              };

    try {
      const res = await fetch(`/api/clients/${client.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          ...pocPayload,
          service_address: addressSave.serviceAddress,
          home_address: addressSave.homeAddress,
          address_line1: addressSave.street,
          city: addressSave.city,
          state: addressSave.state,
          zip: addressSave.zip
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update client chart');
      }

      const data = await res.json();
      onSave(data.client);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error updating client');
    } finally {
      setSaving(false);
    }
  };

  const legacyServiceAddress = legacyServiceAddressForReview({
    serviceAddress: client.service_address,
    street: formData.address_line1,
    city: formData.city,
    state: formData.state,
    zip: formData.zip
  });
  const homeSaved = (formData.home_address || '').trim();

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border border-slate-200/90 rounded-2xl max-w-3xl w-full p-6 space-y-5 shadow-xl my-8">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
              <User className="w-4 h-4 text-slate-700" />
              <span>Edit Client Chart • {client.first_name} {client.last_name}</span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {initialPoc.mode === 'separate'
                ? 'Updates demographics, guardian, physician, support coordinator, and each plan of care. Saving one plan keeps the other.'
                : 'Updates demographics, guardian, physician, support coordinator, and speech plan of care non-destructively.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
            {error}
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6 text-xs max-h-[70vh] overflow-y-auto pr-2">
          {/* Section 1: Demographics & Identifiers */}
          <div className="space-y-3">
            <h4 id="edit-modal-section-demographics" tabIndex={-1} className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-1.5 outline-none">
              <User className="w-3.5 h-3.5 text-slate-600" />
              <span>Demographics & Identifiers</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">First Name *</label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Last Name *</label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Date of Birth (DOB) *</label>
                <input
                  type="date"
                  required
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="DISCHARGED">DISCHARGED</option>
                  <option value="PENDING_INTAKE">PENDING_INTAKE</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Primary Language</label>
                <input
                  type="text"
                  value={formData.primary_language}
                  onChange={(e) => setFormData({ ...formData, primary_language: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. English, Spanish"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">District Code</label>
                <input
                  type="text"
                  value={formData.district_code}
                  onChange={(e) => setFormData({ ...formData, district_code: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. D2, D1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">AHCCCS ID</label>
                <input
                  type="text"
                  value={formData.ahcccs_id}
                  onChange={(e) => setFormData({ ...formData, ahcccs_id: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. A12345678"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">WellSky Case #</label>
                <input
                  type="text"
                  value={formData.wellsky_caseno}
                  onChange={(e) => setFormData({ ...formData, wellsky_caseno: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. 200000..."
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Policy / Member ID</label>
                <input
                  type="text"
                  value={formData.policy_id}
                  onChange={(e) => setFormData({ ...formData, policy_id: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="Insurance Policy #"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">WellSky Client ID</label>
                <input
                  type="text"
                  value={formData.wellsky_client_id}
                  onChange={(e) => setFormData({ ...formData, wellsky_client_id: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. WS-..."
                />
              </div>
            </div>

            {/* Commercial / Secondary TPL Coverage */}
            <div className="pt-2 border-t border-slate-100 space-y-2">
              <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider block">
                Secondary / Commercial TPL Insurance (Distinct from Medicaid)
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Commercial Payer Name</label>
                  <input
                    type="text"
                    value={formData.secondary_payer_name}
                    onChange={(e) => setFormData({ ...formData, secondary_payer_name: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="e.g. Blue Cross Blue Shield"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Commercial Member / Policy ID</label>
                  <input
                    type="text"
                    value={formData.secondary_policy_id}
                    onChange={(e) => setFormData({ ...formData, secondary_policy_id: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="Commercial Member ID"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Group #</label>
                  <input
                    type="text"
                    value={formData.secondary_group_id}
                    onChange={(e) => setFormData({ ...formData, secondary_group_id: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="Group Number"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Subscriber / Insured Name</label>
                  <input
                    type="text"
                    value={formData.insured_name}
                    onChange={(e) => setFormData({ ...formData, insured_name: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="Subscriber full name"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Subscriber Relationship</label>
                  <input
                    type="text"
                    value={formData.subscriber_relationship}
                    onChange={(e) => setFormData({ ...formData, subscriber_relationship: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="Child, Self, Spouse"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Subscriber DOB</label>
                  <input
                    type="date"
                    value={formData.insured_dob}
                    onChange={(e) => setFormData({ ...formData, insured_dob: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Address & Contact */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-600" />
              <span>Addresses & Contacts</span>
            </h4>
            <div className="space-y-2">
              <span className="block text-[11px] font-bold text-slate-800 uppercase tracking-wider">Member address</span>
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <div className="sm:col-span-6">
                  <label className="block text-slate-600 font-semibold mb-1">Street address</label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    autoComplete="address-line1"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-slate-600 font-semibold mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    autoComplete="address-level2"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="block text-slate-600 font-semibold mb-1">State</label>
                  <input
                    type="text"
                    maxLength={2}
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="AZ"
                    autoComplete="address-level1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-600 font-semibold mb-1">ZIP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    placeholder="##### or #####-####"
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-500">
                Enter the street, city, state, and ZIP together. A claim cannot be created until all four are saved.
              </p>
            </div>

            {legacyServiceAddress ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="block text-[11px] font-semibold text-slate-700">Service address on file</span>
                <span className="block text-xs text-slate-800 mt-0.5">{legacyServiceAddress}</span>
                <p className="text-[10px] text-slate-500 mt-1">
                  This is the treatment location already saved. It is separate from the member address and is left unchanged here.
                </p>
              </div>
            ) : null}

            <details className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <summary className="cursor-pointer text-[11px] font-semibold text-slate-800">
                Home address, if different
                {homeSaved ? (
                  <span className="mt-1 block font-normal text-xs text-slate-700">{homeSaved}</span>
                ) : (
                  <span className="mt-1 block font-normal text-[10px] text-slate-500">No separate home address saved.</span>
                )}
              </summary>
              <div className="pt-2">
                <label className="block text-slate-600 font-semibold mb-1">Home address</label>
                <input
                  type="text"
                  value={formData.home_address}
                  onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Use this only when the member lives somewhere other than the treatment location. It is not the member address above.
                </p>
              </div>
            </details>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Client Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Client Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Guardian / Legal Representative */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-1.5">
              <Users className="w-3.5 h-3.5 text-slate-600" />
              <span>Guardian & Legal Representative</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Guardian Full Name</label>
                <input
                  type="text"
                  value={formData.guardian_name}
                  onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="Parent or legal guardian"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Relationship</label>
                <input
                  type="text"
                  value={formData.guardian_relationship}
                  onChange={(e) => setFormData({ ...formData, guardian_relationship: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. Mother, Father, Legal Guardian"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Guardian Phone</label>
                <input
                  type="tel"
                  value={formData.guardian_phone}
                  onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Guardian Email</label>
                <input
                  type="email"
                  value={formData.guardian_email}
                  onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Guardian Address</label>
                <input
                  type="text"
                  value={formData.guardian_address}
                  onChange={(e) => setFormData({ ...formData, guardian_address: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Clinical & Plan of Care (POC) */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <h4 id="edit-modal-section-poc" tabIndex={-1} className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-1.5 outline-none">
              <Calendar className="w-3.5 h-3.5 text-slate-600" />
              <span>{initialPoc.mode === 'separate' ? 'Plans of Care & Clinical Details' : 'Speech Plan of Care (POC) & Clinical Details'}</span>
            </h4>
            {initialPoc.mode === 'unresolved' ? (
              <div id="edit-modal-section-speech" tabIndex={-1} className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2 outline-none">
                <p className="text-[11px] text-amber-900">
                  Competing plan dates are preserved. They are not loaded into date fields, so saving this chart does not clear them.
                </p>
                <p className="font-mono text-[11px] text-slate-800">
                  Start: {client.poc_start_date || '—'}
                </p>
                <p className="font-mono text-[11px] text-slate-800">
                  End: {client.poc_end_date || '—'}
                </p>
                <label className="block text-slate-600 font-semibold mb-1">POC Status</label>
                <select
                  value={speechStatus}
                  onChange={(e) => {
                    setSpeechStatus(e.target.value);
                    setPocEdited(true);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-semibold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  {statusChoices(speechStatus, SINGLE_POC_STATUSES).map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setClearStoredPocDates(true);
                    setPocEdited(true);
                  }}
                  className="text-[11px] font-semibold text-amber-900 underline"
                >
                  {clearStoredPocDates ? 'Stored plan dates will be cleared on save' : 'Clear stored plan dates'}
                </button>
              </div>
            ) : initialPoc.mode === 'separate' ? (
              <div className="space-y-3">
                {[
                  {
                    title: 'Speech Plan of Care',
                    start: speechStart,
                    end: speechEnd,
                    status: speechStatus,
                    setStart: setSpeechStart,
                    setEnd: setSpeechEnd,
                    setStatus: setSpeechStatus
                  },
                  {
                    title: 'Feeding Plan of Care',
                    start: feedingStart,
                    end: feedingEnd,
                    status: feedingStatus,
                    setStart: setFeedingStart,
                    setEnd: setFeedingEnd,
                    setStatus: setFeedingStatus
                  }
                ].map((plan) => (
                  <div key={plan.title} className="rounded-xl border border-slate-200 p-3 space-y-2">
                    <div
                      id={plan.title === 'Feeding Plan of Care' ? 'edit-modal-section-feeding' : 'edit-modal-section-speech'}
                      tabIndex={-1}
                      className="text-[11px] font-bold uppercase tracking-wide text-slate-700 outline-none"
                    >
                      {plan.title}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-slate-600 font-semibold mb-1">Start Date</label>
                        <input
                          type="date"
                          aria-label={`${plan.title} start date`}
                          value={plan.start}
                          onChange={(e) => {
                            plan.setStart(e.target.value);
                            setPocEdited(true);
                          }}
                          className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-600 font-semibold mb-1">End Date</label>
                        <input
                          type="date"
                          aria-label={`${plan.title} end date`}
                          value={plan.end}
                          onChange={(e) => {
                            plan.setEnd(e.target.value);
                            setPocEdited(true);
                          }}
                          className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-600 font-semibold mb-1">Status</label>
                        <select
                          aria-label={`${plan.title} status`}
                          value={plan.status}
                          onChange={(e) => {
                            plan.setStatus(e.target.value);
                            setPocEdited(true);
                          }}
                          className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-semibold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                        >
                          {statusChoices(plan.status, STREAM_POC_STATUSES).map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="space-y-3">
            <div id="edit-modal-section-speech" tabIndex={-1} className="grid grid-cols-1 sm:grid-cols-3 gap-3 outline-none">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">POC Start Date</label>
                <input
                  type="date"
                  value={speechStart}
                  onChange={(e) => {
                    setSpeechStart(e.target.value);
                    setPocEdited(true);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">POC Expiration / End Date</label>
                <input
                  type="date"
                  value={speechEnd}
                  onChange={(e) => {
                    setSpeechEnd(e.target.value);
                    setPocEdited(true);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">POC Status</label>
                <select
                  value={speechStatus}
                  onChange={(e) => {
                    setSpeechStatus(e.target.value);
                    setPocEdited(true);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-semibold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  {statusChoices(speechStatus, SINGLE_POC_STATUSES).map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
            {addingFeeding ? (
              <div id="edit-modal-section-feeding" tabIndex={-1} className="rounded-xl border border-slate-200 p-3 space-y-2 outline-none">
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-700">Feeding Plan of Care</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">Start Date</label>
                    <input
                      type="date"
                      aria-label="Feeding Plan of Care start date"
                      value={feedingStart}
                      onChange={(e) => setFeedingStart(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">End Date</label>
                    <input
                      type="date"
                      aria-label="Feeding Plan of Care end date"
                      value={feedingEnd}
                      onChange={(e) => setFeedingEnd(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-semibold mb-1">Status</label>
                    <select
                      aria-label="Feeding Plan of Care status"
                      value={feedingStatus}
                      onChange={(e) => setFeedingStatus(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-semibold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                    >
                      {statusChoices(feedingStatus, STREAM_POC_STATUSES).map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div id="edit-modal-section-feeding" tabIndex={-1} className="outline-none">
                <button
                  type="button"
                  onClick={() => {
                    setFeedingStart('');
                    setFeedingEnd('');
                    setFeedingStatus('ACTIVE');
                    setAddingFeeding(true);
                  }}
                  className="text-[11px] font-semibold text-slate-800 underline"
                >
                  Add Feeding POC
                </button>
              </div>
            )}
            </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Diagnosis ICD-10 Code</label>
                <input
                  type="text"
                  value={formData.diagnosis_code}
                  onChange={(e) => setFormData({ ...formData, diagnosis_code: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. R62.50, F80.2, F80.0"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Diagnosis Description</label>
                <input
                  type="text"
                  value={formData.diagnosis_description}
                  onChange={(e) => setFormData({ ...formData, diagnosis_description: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. Unspecified lack of expected normal physiological development"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 font-semibold mb-1">Referral / Medical Order Information</label>
              <textarea
                rows={2}
                value={formData.referral_order_info}
                onChange={(e) => setFormData({ ...formData, referral_order_info: e.target.value })}
                className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                placeholder="Prescribing physician order notes, special instructions..."
              />
            </div>
          </div>

          {/* Section 5: Physician & DDD Support Coordinator */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <h4 id="edit-modal-section-physician" tabIndex={-1} className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-1.5 outline-none">
              <Stethoscope className="w-3.5 h-3.5 text-slate-600" />
              <span>Ordering Physician & Care Team</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Physician Name</label>
                <input
                  type="text"
                  value={formData.physician_name}
                  onChange={(e) => setFormData({ ...formData, physician_name: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. Dr. Sarah Jenkins, MD"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Physician NPI</label>
                <input
                  type="text"
                  value={formData.physician_npi}
                  onChange={(e) => setFormData({ ...formData, physician_npi: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs font-mono text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="10-digit NPI"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Practice / Clinic Name</label>
                <input
                  type="text"
                  value={formData.physician_practice}
                  onChange={(e) => setFormData({ ...formData, physician_practice: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Physician Phone</label>
                <input
                  type="tel"
                  value={formData.physician_phone}
                  onChange={(e) => setFormData({ ...formData, physician_phone: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Physician Fax (for POC Recertification)</label>
                <input
                  type="tel"
                  value={formData.physician_fax}
                  onChange={(e) => setFormData({ ...formData, physician_fax: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>

            <div id="edit-modal-sc-section" tabIndex={-1} className="pt-2 border-t border-slate-100 outline-none">
              <h5 className="text-[11px] font-bold text-slate-900 uppercase mb-2 flex items-center justify-between">
                <span>DES/DDD Support Coordinator</span>
                <span className="text-[10px] text-slate-500 font-normal">Support Coordinator & Guardian</span>
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Coordinator Name</label>
                  <input
                    id="edit-modal-sc-name"
                    type="text"
                    value={formData.support_coordinator_name}
                    onChange={(e) => setFormData({ ...formData, support_coordinator_name: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Coordinator Phone</label>
                  <input
                    type="tel"
                    value={formData.support_coordinator_phone}
                    onChange={(e) => setFormData({ ...formData, support_coordinator_phone: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Coordinator Email</label>
                  <input
                    type="email"
                    value={formData.support_coordinator_email}
                    onChange={(e) => setFormData({ ...formData, support_coordinator_email: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t border-slate-100 sticky bottom-0 bg-white py-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 shadow-xs disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              <span>{saving ? 'Saving...' : 'Save Chart Details'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
